'use client'

import { cva, VariantProps } from 'class-variance-authority'
import { isNil } from 'lodash-es'
import { ComponentProps, ElementType, ReactNode } from 'react'

import { cn, isDeleteKeyboardEvent, useFixWidth } from '@/shared/lib'

const chipVariants = cva(
  [
    'flex items-center justify-center rounded-sm border bg-white font-medium transition-all',
    'ring-offset-background focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
    '[&_span]:inline-block [&_span]:w-fit',
    '[&_svg]:size-4',
  ],
  {
    variants: {
      color: {
        gray: 'focus-visible:ring-ring border-gray-200 text-black [&_svg]:text-black',
        purple:
          'border-purple-200 text-purple-500 focus-visible:ring-purple-200 [&_svg]:text-purple-500',
      },
      size: {
        medium: 'typo-body2 h-10 px-[9px] py-2',
        large: 'typo-body1 h-12 px-[9px] py-[11px]',
      },
      selected: {
        true: 'font-bold',
        false: '',
      },
      clickable: {
        true: '',
        false: 'cursor-default',
      },
      disabled: {
        true: 'cursor-default',
        false: '',
      },
    },

    compoundVariants: [
      {
        color: 'gray',
        selected: true,
        className: 'border-black',
      },
      {
        color: 'purple',
        selected: true,
        className: 'border-purple-500',
      },
      {
        color: 'gray',
        disabled: true,
        className: 'text-gray-200 [&_svg]:text-gray-200',
      },
      {
        color: 'purple',
        disabled: true,
        className: 'border-purple-200 text-purple-200 [&_svg]:text-purple-200',
      },
      {
        clickable: true,
        disabled: false,
        className: 'cursor-pointer hover:bg-gray-50/50 active:font-bold',
      },
    ],
  },
)

type ChipProps = VariantProps<typeof chipVariants> & {
  selected?: boolean
  clickable?: boolean
  startIcon?: ReactNode
  endIcon?: ReactNode
  component?: ElementType
  children: ReactNode
}

type Props<E extends ElementType> = ChipProps &
  Omit<ComponentProps<E>, keyof ChipProps>

export const ChoiceChip = <E extends ElementType>({
  color = 'gray',
  size = 'medium',
  selected = false,
  disabled = false,
  clickable: clickableProp = true,
  component,
  startIcon,
  endIcon,
  tabIndex: tabIndexProp = 0,
  ref,
  onClick,
  onKeyDown,
  onKeyUp,
  onDelete,
  width: widthProp,
  children,
  ...props
}: Props<E>) => {
  const { width: widthFromChildren, childrenRef } =
    useFixWidth<HTMLDivElement>(children)

  const $Element = component || 'div'

  const width = isNil(widthProp) ? widthFromChildren : widthProp

  const clickable = clickableProp && onClick ? true : clickableProp

  const tabIndex = ((): number => {
    if (disabled) return -1
    if (!isNil(tabIndexProp)) return tabIndexProp as number

    return clickable ? 0 : -1
  })()

  const role = (() => {
    if (component === 'a') return null
    if (clickable) return 'button'

    return null
  })()

  const handleClick = (event: React.MouseEvent) => {
    if (disabled) return
    onClick?.(event)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.currentTarget === event.target && isDeleteKeyboardEvent(event)) {
      // Will be handled in keyUp, otherwise some browsers
      // might init navigation
      event.preventDefault()
    }

    if (onKeyDown) {
      onKeyDown(event)
    }
  }

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.currentTarget === event.target) {
      if (onDelete && isDeleteKeyboardEvent(event)) {
        onDelete(event)
      }
    }

    if (onKeyUp) {
      onKeyUp(event)
    }
  }

  return (
    <$Element
      ref={ref}
      className={cn(
        chipVariants({ color, size, selected, clickable, disabled }),
      )}
      style={width && { width: `${width}px` }}
      tabIndex={tabIndex}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      aria-disabled={disabled}
      {...(role && { role })}
      {...props}
    >
      <div
        className="flex items-center justify-center whitespace-nowrap"
        ref={childrenRef}
      >
        {startIcon}
        <span>{children}</span>
        {endIcon}
      </div>
    </$Element>
  )
}
